import { createSelector } from '@reduxjs/toolkit';
import { selectActiveCommunityId } from 'community/state/selectors';
import { CommunityId } from 'domain/CommunityId';
import { find } from 'lodash-es';
import prop from 'lodash/fp/prop';
import { selectUserAccountJobTitle } from 'user/api/selectors';

import { communityApi } from './communityApi';

import type { RootState } from 'store';

const selectRootState = (state: RootState) => state;

// My Community
export const selectMyCommunities = communityApi.endpoints.getMyCommunities.select();

// The user may be viewing a community page they don't belong to yet
// (e.g., Register Natter Event page or Open Natter Availability Submission page).
// In that case, the active community id could be outside the list of their communities,
// and selectMyCommunity should return "undefined".
export const selectMyCommunity = createSelector(
  [selectActiveCommunityId, selectMyCommunities],
  (activeCommunityId, myCommunitiesDataResult) =>
    activeCommunityId ? find(myCommunitiesDataResult?.data?.myCommunities, { id: activeCommunityId }) : undefined
);

export const selectMyCommunityId = createSelector(selectMyCommunity, prop('id'));
export const selectMyCommunityHash = createSelector(selectMyCommunity, prop('hash'));
export const selectMyCommunityName = createSelector(selectMyCommunity, prop('name'));
export const selectCommunitySettings = createSelector(selectMyCommunity, prop('communitySettings'));
export const selectMyCommunityOwnerId = createSelector(selectMyCommunity, prop('ownerId'));
export const selectMyCommunityAutomaticOptOutEnabled = createSelector(
  selectCommunitySettings,
  prop('automaticOptOutEnabled')
);

export const selectIsOpenNattersEnabledForMyCommunity = createSelector(
  selectCommunitySettings,
  prop('openNatterEnabled')
);

export const selectIsNatalieEventsEnabledForMyCommunity = createSelector(
  selectCommunitySettings,
  prop('natalieEnabled')
);

export const selectIsChatV2EnabledForMyCommunity = createSelector(selectCommunitySettings, prop('chatV2Enabled'));

/**
 * Whether the active community has turned the job-title field off (HNS-404).
 *
 * Display-only — stored job titles are never cleared, so turning the setting back on restores them.
 */
export const selectIsJobTitleHiddenForMyCommunity = createSelector(
  selectCommunitySettings,
  (communitySettings) => communitySettings?.roleFieldDisabled ?? false
);

export const selectHasNoCommunity = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.data?.hasNoCommunity
);
export const selectMyCommunityIsLoading = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.isLoading
);
export const selectMyCommunityIsError = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.isError
);
export const selectMyCommunitiesIds = createSelector(selectMyCommunities, (myCommunityDataResult) =>
  myCommunityDataResult?.data?.myCommunities.map((c) => c.id)
);
export const selectIsPartOfCommunity = (communityId: CommunityId | undefined) =>
  createSelector(selectMyCommunitiesIds, (myCommunities) =>
    communityId ? myCommunities?.includes(communityId) : false
  );

// Community Member
export const selectCommunityMemberByCommunityId = (state: RootState, id: CommunityId | undefined) =>
  communityApi.endpoints.getCurrentCommunityMember.select({ communityId: id })(state)?.data;

export const selectCurrentCommunityMember = createSelector(selectRootState, selectMyCommunityId, (state, id) =>
  selectCommunityMemberByCommunityId(state, id)
);

export const selectCurrentCommunityMemberIsLoading = createSelector(
  selectRootState,
  selectMyCommunityId,
  (state, id) => communityApi.endpoints.getCurrentCommunityMember.select({ communityId: id })(state).isLoading
);

export const selectCommunityMemberConfigurationInfo = createSelector(
  selectMyCommunity,
  selectCurrentCommunityMember,
  selectCurrentCommunityMemberIsLoading,
  selectUserAccountJobTitle,
  (myCommunity, communityMember, isLoading, accountJobTitle) => {
    const isRegistered = !!communityMember;
    const roleFieldDisabled = myCommunity?.communitySettings?.roleFieldDisabled;
    // The job title lives on the account, so a title set in another community counts here too.
    const isRoleConfigured = !!(communityMember?.role || accountJobTitle);

    const isAccountConfigured = !!(communityMember?.displayName && (roleFieldDisabled || isRoleConfigured));

    return {
      roleFieldDisabled,
      isRoleConfigured,
      isAccountConfigured,
      isRegistered,
      isLoading,
    };
  }
);

export const selectCommunityBySlug = (slug: string | undefined | null) =>
  createSelector(selectMyCommunities, (myCommunitiesDataResult) =>
    slug ? find(myCommunitiesDataResult?.data?.myCommunities, { slug }) : undefined
  );
